import React, { useCallback, useEffect, useState } from 'react';
import { ActivityIndicator, Button, HelperText } from 'react-native-paper';
import { CompositeScreenProps } from '@react-navigation/native';
import { StackScreenProps } from '@react-navigation/stack';
import { useMutation } from 'react-query';
import { Platform } from 'react-native';
import { getTimeZone } from 'react-native-localize';
import { useI18n } from '../../context/I18nContext';
import ScrollablePage from '../../components/ui/ScrollablePage';
import { InformationStackRouteParams } from '../../navigators/InformationStackNavigator';
import { AppDrawerScreenProp } from '../../components/DrawerMenuContent';
import { ThirdPartyAuthProvider } from '../../types/appsync-types';
import { startThirdPartyAuth, useGetMyThirdPartyAuthByProvider } from '../../apis/appsyncApis';
import { screenNames } from '../../navigators/screenNames';
import Typography from '../../components/Typography';
import Container from '../../components/Container';

export type ThirdPartyAuthDetailRouteParams = {
  authProvider: ThirdPartyAuthProvider;
  status?: 'error' | 'success';
  reason?: string;
};

type Props = CompositeScreenProps<
  StackScreenProps<InformationStackRouteParams, 'ThirdPartyAuthDetail'>,
  AppDrawerScreenProp
>;

export const ThirdPartyAuthDetail: React.FC<Props> = ({ navigation, route }) => {
  const { I18n, formatDate } = useI18n();

  const thirdPartyAuthStatus = route.params.status;

  useEffect(() => {
    if (thirdPartyAuthStatus === 'success') {
      navigation.pop();
    }
  }, [navigation, thirdPartyAuthStatus]);

  const thirdPartyAuthProvider = route.params.authProvider;
  const thirdPartyAuthStatusReason = route.params.reason;

  let errorText = thirdPartyAuthStatus === 'error' ? I18n.t('thirdPartyAuth.error') : null;
  if (errorText && thirdPartyAuthStatusReason) errorText += `: ${thirdPartyAuthStatusReason}`;

  const [errorMessage, setErrorMessage] = useState<string | null>(errorText);

  const getMyThirdPartyAuthByProviderResult = useGetMyThirdPartyAuthByProvider(thirdPartyAuthProvider);
  const thirdPartyAuth = getMyThirdPartyAuthByProviderResult.data?.data?.getThirdPartyAuthByProvider;

  const startThirdPartyAuthMutation = useMutation(
    async (input: Parameters<typeof startThirdPartyAuth>[number]) => startThirdPartyAuth(input),
    {},
  );

  const thirdPartyAuthMutationAsync = useCallback(async () => {
    setErrorMessage(null);
    try {
      const result = await startThirdPartyAuthMutation.mutateAsync({
        input: { authProvider: thirdPartyAuthProvider },
      });
      const authUrl = result.data?.startThirdPartyAuth?.authUrl;
      if (!authUrl) {
        setErrorMessage(I18n.t('thirdPartyAuth.startThirdPartyAuthFailed'));
        return;
      }
      if (Platform.OS === 'web') {
        window.location.href = authUrl;
      } else {
        navigation.navigate(screenNames.ThirdPartyAuthWebView, {
          url: authUrl,
          authProvider: thirdPartyAuthProvider,
        });
      }
    } catch (error) {
      const errorString = JSON.stringify(error);
      console.log(errorString);
      setErrorMessage(I18n.t('thirdPartyAuth.startThirdPartyAuthFailed'));
    }
  }, [I18n, navigation, startThirdPartyAuthMutation, thirdPartyAuthProvider]);

  const renderAuthContent = () => {
    return thirdPartyAuth ? (
      <Typography variant="body1" style={{ marginVertical: 16 }}>
        {I18n.t('thirdPartyAuth.authenticatedAt', {
          date: thirdPartyAuth.createdAt
            ? formatDate(new Date(thirdPartyAuth.createdAt), 'dateWithYear', getTimeZone())
            : '',
        })}
      </Typography>
    ) : (
      <Button
        mode="outlined"
        style={{ marginVertical: 16 }}
        onPress={thirdPartyAuthMutationAsync}
        loading={startThirdPartyAuthMutation.isLoading}
        disabled={startThirdPartyAuthMutation.isLoading}
      >
        {I18n.t('thirdPartyAuth.authenticate')}
      </Button>
    );
  };

  return (
    <ScrollablePage>
      <Container style={{ marginVertical: 16 }}>
        {getMyThirdPartyAuthByProviderResult.isLoading ? <ActivityIndicator /> : renderAuthContent()}
        {!!errorMessage && (
          <HelperText visible type={'error'} style={{ marginBottom: 16, textAlign: 'center' }}>
            {errorMessage}
          </HelperText>
        )}
      </Container>
    </ScrollablePage>
  );
};
